import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../config/api';
import { ChatSession, ChatSessionCreate, ChatSessionUpdate, ChatSessionList } from '../types/chat';

export const useChatSessions = (
  bookId: string | null,
  courseId: string | null = null,
  courseItemId: string | null = null,
) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  // Fetch all sessions for the book
  const fetchSessions = useCallback(async () => {
    if (!bookId || fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<ChatSessionList>('/api/chat/sessions', {
        params: {
          session_type: 'book_chat',
          book_id: bookId,
          ...(courseItemId ? { course_item_id: courseItemId } : {})
        }
      });
      setSessions(response.data.sessions);
      setActiveSessionId(response.data.active_session_id || null);

      // No sessions? Create one — tagged to the course reading when applicable
      // so it stays separate from normal book reading.
      if (response.data.sessions.length === 0) {
        const createResponse = await api.post<ChatSession>(
          '/api/chat/sessions',
          {
            book_id: bookId,
            session_type: 'book_chat',
            session_name: 'Chat 1',
            is_active: true,
            ...(courseId && courseItemId
              ? { session_metadata: { source: 'course', course_id: courseId, course_item_id: courseItemId } }
              : {})
          }
        );
        setSessions([createResponse.data]);
        setActiveSessionId(createResponse.data.id);
      }
    } catch (err) {
      setError('Failed to fetch chat sessions');
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [bookId, courseId, courseItemId]);

  // Create a new session
  const createSession = useCallback(async (sessionData: ChatSessionCreate) => {
    try {
      const response = await api.post<ChatSession>(
        '/api/chat/sessions',
        sessionData
      );

      // If it's active, update all sessions properly
      if (response.data.is_active) {
        setActiveSessionId(response.data.id);
        // Update sessions: deactivate others and add the new one
        setSessions(prev => [
          ...prev.map(s => ({ ...s, is_active: false })),
          response.data
        ]);
      } else {
        // Just add the new session
        setSessions(prev => [...prev, response.data]);
      }

      return response.data;
    } catch (err) {
      console.error('Error creating session:', err);
      throw err;
    }
  }, [bookId]);

  // Update a session (rename, etc)
  const updateSession = useCallback(async (sessionId: string, updateData: ChatSessionUpdate) => {
    try {
      const response = await api.patch<ChatSession>(
        `/api/chat/sessions/${sessionId}`,
        updateData
      );

      // Update local state
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? response.data : s
      ));

      return response.data;
    } catch (err) {
      console.error('Error updating session:', err);
      throw err;
    }
  }, []);

  // Activate a session
  const activateSession = useCallback(async (sessionId: string) => {
    try {
      await api.post(`/api/chat/sessions/${sessionId}/activate`);

      // Update local state
      setActiveSessionId(sessionId);
      setSessions(prev => prev.map(s => ({
        ...s,
        is_active: s.id === sessionId
      })));
    } catch (err) {
      console.error('Error activating session:', err);
      throw err;
    }
  }, []);

  // Get active session
  const getActiveSession = useCallback(() => {
    return sessions.find(s => s.is_active) || null;
  }, [sessions]);

  // Update session title (for auto-generated titles from backend)
  const updateSessionTitle = useCallback(async (sessionId: string, title: string) => {
    try {
      // Persist to backend
      await api.patch(`/api/chat/sessions/${sessionId}`, { title });

      // Update local state
      setSessions(prevSessions =>
        prevSessions.map(session =>
          session.id === sessionId
            ? { ...session, title }
            : session
        )
      );
    } catch (err) {
      console.error('Error updating session title:', err);
      // Still update local state even if backend fails (for better UX)
      setSessions(prevSessions =>
        prevSessions.map(session =>
          session.id === sessionId
            ? { ...session, title }
            : session
        )
      );
    }
  }, []);

  // Initial fetch when bookId changes
  useEffect(() => {
    if (bookId) {
      fetchSessions();
    }
  }, [bookId, fetchSessions]);

  return {
    sessions,
    activeSessionId,
    loading,
    error,
    createSession,
    updateSession,
    updateSessionTitle,
    activateSession,
    getActiveSession,
    refreshSessions: fetchSessions
  };
};